/* noa-art-wall.js — the wall planner.
 *
 * Everything on the wall is positioned and sized in CENTIMETRES and converted to
 * pixels through one factor (pxPerCm), so the arrangement stays physically honest
 * whatever the room or the viewport.
 *
 * The background is ALWAYS a real photographed room — one of the 4 built-in rooms,
 * or one the visitor uploads. Two earlier attempts were rejected: a flat coloured
 * wall ("ברגע שאני משנה צבע התמונה ברקע נעלמת ונהיה האיור המופשט שאני לא רוצה") and
 * a translucent multiply tint over the photo, and then offline-repainted variants of
 * each room — that last one Stas rejected too ("הצביעה של הקירות יצאה ממש לא טוב").
 * His instruction: "במקום שתחליף את הצבע של הקיר ברקע פשוט שים תמונת רקע אחרת שבה
 * הקיר מלכתחילא היה בצבע אחר". So there is NO recolouring of any kind. The palette is
 * simply six REAL rooms whose walls happen to be different colours, and picking a
 * wall means picking a room.
 *
 * Each room declares a HANGABLE REGION (fractions of the photo) and how wide that
 * region is in real cm — an estimate from a known object in the shot (a 160cm bench,
 * an 80cm rocking horse) — exposed as an editable field rather than hidden, since a
 * user who knows the room can correct it and fix every size at once.
 *
 * State round-trips through the URL hash, which is what makes "share" a one-liner.
 * An uploaded photo never leaves the browser, so it cannot be encoded into that link.
 */
(function () {
  'use strict';
  var D = window.NOA_WALL;
  if (!D) return;

  var $ = function (id) { return document.getElementById(id); };
  var box = $('noaBox'), inner = $('noaInner'), grid = $('noaGrid'), tabs = $('noaTabs');
  if (!box) return;

  var ROOMS = (D.rooms || []).slice();
  // the stored urls have no query string, so pick the right separator
  function sized(u, w) { return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + w; }
  var ROOM_BY = {};
  ROOMS.forEach(function (r) { ROOM_BY[r.k] = r; });

  /* A room the visitor uploaded. It lives only in this tab: we never send the photo
     anywhere, so it cannot be encoded into the share link either. Its hangable region
     is the whole photo — we cannot guess where their wall is, so they position the art
     themselves and give the one number that sets the scale: the wall's real width. */
  var CUSTOM = null;

  /* ---------- product index ---------- */
  // size label -> {w,h} in cm. Handles '22.5×30 ס״מ', 'פטיט · 22.5×30', '25×25 ס״מ'.
  function cm(label) {
    var s = String(label).replace(/[×*]/g, 'x');
    if (s.indexOf('·') > -1) s = s.split('·').pop();
    var m = s.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/);
    return m ? { w: parseFloat(m[1]), h: parseFloat(m[2]) } : null;
  }

  var BY_HANDLE = {};
  D.collections.forEach(function (c) {
    c.products.forEach(function (p) {
      p.coll = c.handle;
      p.sizes = p.v.map(function (v) {
        var d = cm(v.s);
        if (!d) return null;
        /* 🔴 The variant LABEL is not a trustworthy source of ORIENTATION. Every
           Mothers product is sold as '15×22.5' while its artwork is 3:2 LANDSCAPE, so the
           frame came out portrait and object-fit:cover then cropped the mother or the child
           out of the picture. The artwork's own aspect ratio is the only honest source, so
           swap the label's two numbers whenever they disagree with it. The 2% tolerance
           leaves square art (ar≈1) alone. The label itself is kept verbatim — it is what
           the customer is charged for, and correcting it is a product-data decision. */
        var sw = d.w, sh = d.h, ar = +p.ar || 1;
        if ((ar > 1.02 && sw < sh) || (ar < 0.98 && sw > sh)) { var t = sw; sw = sh; sh = t; }
        return { id: v.id, label: v.s, w: sw, h: sh, price: v.p, av: v.av };
      }).filter(Boolean).sort(function (a, b) { return a.w * a.h - b.w * b.h; });
      if (p.sizes.length) BY_HANDLE[p.h] = p;
    });
  });

  function money(cents) {
    return '₪' + (cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ---------- state ---------- */
  var S = {
    room: ROOMS.length ? ROOMS[0].k : null,
    roomCm: {},          // per-room override of the region width in cm
    frames: true, items: []
  };
  var seq = 0, sel = null;

  function baseRoom() {
    if (S.room === 'custom') return CUSTOM;      // null until they upload one
    return ROOM_BY[S.room] || null;
  }
  function room() { return baseRoom(); }
  function roomWidthCm() {
    var r = room();
    return r ? (S.roomCm[r.k] || r.wallCm) : 300;
  }

  function encode() {
    var o = {
      rm: S.room, rc: S.roomCm, f: S.frames ? 1 : 0,
      i: S.items.map(function (it) { return [it.handle, it.si, Math.round(it.x), Math.round(it.y)]; })
    };
    return btoa(unescape(encodeURIComponent(JSON.stringify(o))));
  }
  function decode(str) {
    try {
      var o = JSON.parse(decodeURIComponent(escape(atob(str))));
      if (o.rm === 'custom') {
        // the photo isn't in the link — fall back to the first built-in room
        S.room = ROOMS.length ? ROOMS[0].k : null;
      } else if (ROOM_BY[o.rm]) { S.room = o.rm; }
      S.roomCm = o.rc || {};
      S.frames = o.f !== 0;
      S.ruler = false;
      S.items = (o.i || []).map(function (a) {
        var p = BY_HANDLE[a[0]];
        if (!p) return null;
        var si = Math.min(Math.max(+a[1] || 0, 0), p.sizes.length - 1);
        return { uid: ++seq, handle: a[0], si: si, x: +a[2] || 0, y: +a[3] || 0 };
      }).filter(Boolean);
      return true;
    } catch (e) { return false; }
  }

  /* ---------- geometry ----------
   * `_k` is the one scale factor. It is limited by BOTH the available width and a
   * sane on-screen height (fitting a wide room photo to the width alone produced an
   * unreachable stage on short viewports). WALL_W / WALL_H are the hangable area in
   * real cm. */
  var _k = 1, WALL_W = 300, WALL_H = 200;
  function pxPerCm() { return _k; }

  /* The theme's header height is not knowable from CSS (announcement bar, wrapped
     nav), so a calc(100vh - Npx) either overflows the window or leaves a gap. Measure
     the grid's actual top and hand it the remainder — Stas wants the whole tool usable
     in one window with no page scrolling. */
  /* Anything fixed and full-width sitting on the bottom edge covers the tool's bottom
     bar while still counting towards window.innerHeight: Shopify's theme-preview bar is
     the case Stas hit, and its host element carries a randomised id, so match on shape.
     Narrow floating things (our toast, the WhatsApp bubble) are not bars. */
  function bottomOverlayHeight() {
    var vh = window.innerHeight, vw = window.innerWidth, h = 0;
    var all = document.body ? document.body.children : [];
    for (var i = 0; i < all.length; i++) {
      var e = all[i];
      if (e.id === 'noaToast' || e.closest && e.closest('#noaWall')) continue;
      var cs = getComputedStyle(e);
      if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden') continue;
      var r = e.getBoundingClientRect();
      if (r.height < 24 || r.height > 140) continue;
      if (r.width < vw * 0.6) continue;               // a bar, not a floating button
      if (r.bottom < vh - 6 || r.top > vh) continue;  // must sit on the bottom edge
      h = Math.max(h, Math.round(r.bottom - Math.max(r.top, 0)));
    }
    return h;
  }

  function fitLayout() {
    var lay = document.querySelector('.noa-wall__layout');
    if (!lay) return;
    if (window.innerWidth <= 1020) { lay.style.height = ''; return; }  // stacked mode
    lay.style.height = '';
    var top = lay.getBoundingClientRect().top + window.scrollY;
    /* Shopify's theme-preview bar is a fixed overlay INSIDE the page, so
       window.innerHeight counts the strip it covers and the bottom bar ended up
       underneath it. That is the whole "HIDE BAR breaks the view" report. */
    var covered = bottomOverlayHeight();
    var h = Math.max(460, Math.round(window.innerHeight - top - covered - 10));
    lay.style.height = h + 'px';

    /* No column maths here any more. The CSS gives the two bars fixed widths against the
       page edges and the centre track everything left over, so the room's aspect can
       never shift a side column — the photo just letterboxes inside the middle. */
    lay.style.gridTemplateColumns = '';
  }

  function sizeStage() {
    /* Measure the stage container, not the viewport. The page is now a
       fixed-height three-column grid, so the wall must fill exactly the space the
       other rows leave — a viewport fraction either overflowed or left a gap. */
    var host = box.parentElement;                     // .noa-stagebody
    var cs = host ? getComputedStyle(host) : null;
    var padX = cs ? (parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight)) : 0;
    var padY = cs ? (parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom)) : 0;
    var availW = (host ? host.clientWidth : 900) - padX - 2;
    var availH = (host ? host.clientHeight : 500) - padY - 2;
    // on a narrow screen the grid collapses and the host has no fixed height
    var maxH = availH > 220 ? availH : Math.max(320, Math.round(window.innerHeight * 0.6));
    var r = room();
    var ratio = r ? r.ratio : 1.5;

    /* The BOX is a fixed window — it must not change when the room changes, which is what
       made the photo look like it was zooming. What is held constant is the WALL: every room
       carries exactly 275 cm between its skirting (wb) and its ceiling (wt), so 275 cm is
       mapped to the same pixel height everywhere. px/cm therefore does not depend on the
       room at all, and a 60 cm print is the same size on screen in all of them. */
    /* The box must be no wider than the narrowest photo, or the stage shows empty area
       beside the picture that a print can be dragged into. Measured across the 13 rooms the
       narrowest photo is 1.371x its own wall height, so 1.35x is covered by all of them. */
    /* The wall is mapped to 0.878 of the box height — that is the widest wall band among the
       13 rooms (hall), so every photo is at least as tall as the box and none of them
       leaves an uncovered strip. */
    /* 🔴 Branch on the VIEWPORT WIDTH, never on availH. On a phone the layout is stacked and
       the stage has no fixed height, so availH is the height of the box this function wrote
       on the previous pass — reading it back made the box chase itself a few pixels at a
       time, which is the pulsing Stas saw. Width is an input nothing here writes. */
    var stacked = window.innerWidth <= 1020;
    var baseH = stacked ? Math.max(220, Math.round(availW / 1.3)) : maxH;
    var wallH = Math.round(baseH * 0.878);
    var bw = Math.max(220, Math.min(Math.round(availW), Math.round(wallH * 1.35)));
    /* On a phone the stage has no fixed height, and the old fallback used
       window.innerHeight — which changes as the address bar collapses while you scroll, so
       the wall visibly rescaled mid-scroll. Derive the height from the WIDTH instead: the
       width does not move. */
    var bh = Math.round(baseH);
    box.style.width = bw + 'px';
    box.style.height = bh + 'px';
    box.style.margin = '0 auto';

    if (r && r.k === 'custom') {
      /* 🔴 An UPLOADED room must not go through the fixed 275cm wall band. We do not
         know where its ceiling or its skirting are, so the wt/wb fallback was pure
         invention: it scaled the photo to 1.35x the wall height and the fixed window then
         cut 25% off an ordinary 4:3 phone photo and 44% off a 16:9 one. Show the photo
         WHOLE instead — contained in the box, letterboxed, never cropped — exactly what the
         comment on CUSTOM always said it did.
         Scale comes from the one number only the visitor can know: the wall width they type
         in the calibration field. That field used to be inert (_k was hardwired to
         wallPx/275), which is why sizes on an uploaded wall were meaningless.
         The BOX keeps the same pixel size as for every other room, so switching in and out
         of the uploaded room still does not resize the stage. */
      var fitW = Math.min(bw, Math.round(bh * ratio));
      var fitH = Math.max(1, Math.round(fitW / ratio));
      var offX = Math.round((bw - fitW) / 2), offY = Math.round((bh - fitH) / 2);
      box.style.backgroundImage = 'url("' + r.img + '")';
      box.style.backgroundRepeat = 'no-repeat';
      box.style.backgroundSize = fitW + 'px ' + fitH + 'px';
      box.style.backgroundPosition = offX + 'px ' + offY + 'px';

      // the hangable field is the PHOTO, not the box — so a print cannot be dragged onto
      // the letterbox margin. clampItem() follows WALL_W/WALL_H, which follow this rect.
      inner.style.right = 'auto';
      inner.style.bottom = 'auto';
      inner.style.left = offX + 'px';
      inner.style.top = offY + 'px';
      inner.style.width = fitW + 'px';
      inner.style.height = fitH + 'px';

      var cmW = roomWidthCm();                     // the typed width spans the photo's width
      _k = fitW / cmW;
      WALL_W = cmW;
      WALL_H = fitH / _k;
      $('noaRoomCm').value = Math.round(cmW);
    } else if (r) {
      // Place the photo behind the fixed window so its wall band lands on a fixed strip.
      var wt = (typeof r.wt === 'number') ? r.wt : 0.06;
      var wb = (typeof r.wb === 'number') ? r.wb : 0.80;
      var bandF = Math.max(0.2, wb - wt);
      var wallPx = wallH;                          // the wall's on-screen height, fixed
      var photoH = wallPx / bandF;
      var photoW = photoH * ratio;
      // centre the wall band in the box, so the ceiling and the floor are trimmed evenly
      var top = Math.round((bh - wallPx) / 2 - wt * photoH);
      box.style.backgroundImage = 'url("' + (r.k === 'custom' ? r.img : sized(r.img, 1600)) + '")';
      box.style.backgroundRepeat = 'no-repeat';
      box.style.backgroundSize = Math.round(photoW) + 'px ' + Math.round(photoH) + 'px';
      box.style.backgroundPosition = Math.round((bw - photoW) / 2) + 'px ' + top + 'px';

      // 🔴 right/bottom must stay cleared: .noa-wallbox__inner has inset:0 and on this RTL
      // page a box with left+width+right all set is over-constrained, so `left` gets dropped.
      inner.style.right = 'auto';
      inner.style.bottom = 'auto';
      inner.style.left = '0';
      inner.style.top = '0';
      inner.style.width = '100%';
      inner.style.height = '100%';

      _k = wallPx / 275;                            // the same in every room
      WALL_W = bw / _k;
      WALL_H = bh / _k;
      $('noaRoomCm').value = Math.round(r.wallCm);
    } else {
      // no rooms configured at all — a flat placeholder so the tool still works
      box.style.backgroundImage = 'none';
      box.style.background = '#e9e4dc';
      inner.style.left = 0; inner.style.top = 0; inner.style.width = '100%'; inner.style.height = '100%';
      WALL_W = 300; WALL_H = 200; _k = Math.min(availW / WALL_W, maxH / WALL_H);
    }
    /* Width only. The height used to be shown too, but it is the photo's vertical span,
       not the room's ceiling height, and printing both next to each other read as a claim
       about the room that these AI-generated photos cannot actually support. */
    // 'the wall is 275cm' is a fact about the 13 stock photos only — never claim it about
    // an uploaded room, where the width the visitor typed is the whole of what we know.
    $('noaPxCm').textContent = (r && r.k === 'custom'
      ? 'רוחב הקיר שהוזן ' + Math.round(WALL_W) + ' ס״מ · גובה ' + Math.round(WALL_H) + ' ס״מ'
      : 'גובה הקיר 275 ס״מ · שדה הצפייה ' + Math.round(WALL_W) + ' ס״מ') +
      ' · 1 ס״מ ≈ ' + _k.toFixed(2) + ' px';
  }

  /* Keep the composition when the wall's dimensions change (switching rooms,
     correcting the calibration field): preserve each item's centre as a FRACTION of
     the wall, rather than its absolute cm offset, so the arrangement survives instead
     of being reclamped into a corner. */
  function remapItems(oldW, oldH, newW, newH) {
    if (!(oldW > 0 && oldH > 0)) return;
    S.items.forEach(function (it) {
      var s = BY_HANDLE[it.handle].sizes[it.si];
      var cx = (it.x + s.w / 2) / oldW, cy = (it.y + s.h / 2) / oldH;
      it.x = cx * newW - s.w / 2;
      it.y = cy * newH - s.h / 2;
      clampItem(it);
    });
  }

  function clampItem(it) {
    var s = BY_HANDLE[it.handle].sizes[it.si];
    it.x = Math.min(Math.max(it.x, 0), Math.max(0, WALL_W - s.w));
    it.y = Math.min(Math.max(it.y, 0), Math.max(0, WALL_H - s.h));
  }

  /* ---------- render ---------- */
  function renderItems() {
    var k = pxPerCm();
    inner.innerHTML = '';
    S.items.forEach(function (it) {
      var p = BY_HANDLE[it.handle], s = p.sizes[it.si];
      var el = document.createElement('div');
      el.className = 'noa-art' + (sel === it.uid ? ' is-sel' : '');
      el.dataset.uid = it.uid;
      el.style.width = (s.w * k) + 'px';
      el.style.height = (s.h * k) + 'px';
      el.style.right = (it.x * k) + 'px';   /* RTL wall: x measured from the right */
      el.style.top = (it.y * k) + 'px';
      var pad = S.frames ? Math.max(1.5, Math.min(s.w, s.h) * k * 0.035) : 0;
      var mat = S.frames ? Math.max(1.5, Math.min(s.w, s.h) * k * 0.055) : 0;
      var html = '';
      if (S.frames) html += '<div class="noa-art__frame"></div>';
      html += '<div class="noa-art__mat" style="inset:' + pad + 'px;background:' +
        (S.frames ? 'var(--mat)' : 'transparent') + '">' +
        '<div style="position:absolute;inset:' + mat + 'px;overflow:hidden">' +
        '<img src="' + p.img + '" alt="' + p.t.replace(/"/g, '&quot;') + '" draggable="false">' +
        '</div></div>';
      html += '<span class="noa-art__tag">' + p.t + ' · ' + s.label + '</span>';
      el.innerHTML = html;
      inner.appendChild(el);
    });
    if (S.ruler) {
      var ru = document.createElement('div');
      ru.className = 'noa-ruler';
      ru.style.width = (100 * k) + 'px';
      ru.style.right = (10 * k) + 'px';
      ru.style.bottom = (10 * k) + 'px';
      ru.innerHTML = '<span>100 ס״מ</span>';
      inner.appendChild(ru);
    }
    $('noaEmpty').style.display = S.items.length ? 'none' : 'flex';
    renderSummary();
    renderEditor();
  }

  function renderSummary() {
    var total = S.items.reduce(function (a, it) {
      return a + BY_HANDLE[it.handle].sizes[it.si].price;
    }, 0);
    $('noaCount').textContent = S.items.length + (S.items.length === 1 ? ' עבודה על הקיר' : ' עבודות על הקיר');
    $('noaTotal').textContent = S.items.length ? 'סה״כ ' + money(total) : '';
    $('noaBuy').disabled = !S.items.length;
    $('noaBuy').style.opacity = S.items.length ? 1 : .45;
  }

  function renderEditor() {
    var ed = $('noaEditor');
    var it = S.items.filter(function (x) { return x.uid === sel; })[0];
    if (!it) { ed.classList.remove('is-on'); return; }
    var p = BY_HANDLE[it.handle];
    ed.classList.add('is-on');
    $('noaEdName').textContent = p.t;
    $('noaEdSize').innerHTML = p.sizes.map(function (s, i) {
      return '<option value="' + i + '"' + (i === it.si ? ' selected' : '') + '>' +
        s.label + ' — ' + money(s.price) + (s.av ? '' : ' (אזל)') + '</option>';
    }).join('');
    $('noaEdPrice').textContent = money(p.sizes[it.si].price);
  }

  function renderBgStrip() {
    var s = $('noaBgStrip');
    var all = ROOMS.concat(CUSTOM ? [CUSTOM] : []);
    // group by room type, so the tiles read as "which room" then "which wall colour"
    var cats = [], byCat = {};
    all.forEach(function (r) {
      var c = r.cat || 'שלכם';
      if (!byCat[c]) { byCat[c] = []; cats.push(c); }
      byCat[c].push(r);
    });
    s.innerHTML = cats.map(function (c) {
      return '<div class="noa-bggroup"><span class="noa-bggroup__title">' + c + '</span>' +
        '<div class="noa-bggroup__row">' +
        byCat[c].map(function (r) {
          return '<button class="noa-bg" type="button" data-room="' + r.k + '" title="' +
            c + ' · ' + r.label + '" aria-pressed="' + (S.room === r.k) + '">' +
            '<img src="' + (r.k === 'custom' ? r.img : sized(r.img, 180)) + '" alt="' +
            r.label + '" loading="lazy">' +
            '<span>' + r.label + '</span></button>';
        }).join('') + '</div></div>';
    }).join('');
  }

  function renderAll() { fitLayout(); sizeStage(); renderItems(); renderBgStrip(); }

  /* ---------- picker ---------- */
  var filter = 'all';
  function renderPicker() {
    tabs.innerHTML = ['<button class="noa-pick__tab" data-c="all" aria-pressed="' +
      (filter === 'all') + '">הכל</button>'].concat(
      D.collections.map(function (c) {
        return '<button class="noa-pick__tab" data-c="' + c.handle + '" aria-pressed="' +
          (filter === c.handle) + '">' + c.title + '</button>';
      })).join('');
    var list = Object.keys(BY_HANDLE).map(function (h) { return BY_HANDLE[h]; })
      .filter(function (p) { return filter === 'all' || p.coll === filter; });
    grid.innerHTML = list.map(function (p) {
      return '<button class="noa-pick__item" type="button" data-h="' + p.h + '" title="' +
        p.t.replace(/"/g, '&quot;') + '"><img src="' + p.img + '" alt="' +
        p.t.replace(/"/g, '&quot;') + '" loading="lazy"></button>';
    }).join('');
  }

  function addItem(handle) {
    var p = BY_HANDLE[handle];
    if (!p) return;
    var si = Math.min(1, p.sizes.length - 1);
    var s = p.sizes[si];
    /* Placement is unrestricted once a print is on the wall, but a NEW one should
       still land on the bare wall rather than on the floor — so the search starts
       inside the room's `reg` rectangle and only then falls back to the full frame. */
    /* Land a new print on the bare wall: the wall strip occupies a known part of the
       fixed window, so express the drop area in those terms rather than per-photo. */
    var bh_cm = WALL_H, bw_cm = WALL_W;
    var rx = bw_cm * 0.12, rw = bw_cm * 0.76;
    var ry = bh_cm * 0.10, rh = bh_cm * 0.42;
    var it = { uid: ++seq, handle: handle, si: si,
               x: Math.max(2, rx + (rw - s.w) / 2), y: Math.max(2, ry + (rh - s.h) / 2) };
    var free = !S.items.some(function (o) {
      var os = BY_HANDLE[o.handle].sizes[o.si];
      return !(it.x + s.w + 3 < o.x || o.x + os.w + 3 < it.x ||
               it.y + s.h + 3 < o.y || o.y + os.h + 3 < it.y);
    });
    outer: for (var y = free ? 1e9 : ry + 2; y < ry + rh - s.h; y += 4) {
      for (var x = rx + 2; x < rx + rw - s.w; x += 4) {
        var clash = S.items.some(function (o) {
          var os = BY_HANDLE[o.handle].sizes[o.si];
          return !(x + s.w + 3 < o.x || o.x + os.w + 3 < x || y + s.h + 3 < o.y || o.y + os.h + 3 < y);
        });
        if (!clash) { it.x = x; it.y = y; break outer; }
      }
    }
    S.items.push(it);
    sel = it.uid;
    clampItem(it);
    renderItems();
    toast(p.t + ' נוסף לקיר');
  }

  /* ---------- drag ---------- */
  var drag = null;
  inner.addEventListener('pointerdown', function (e) {
    var el = e.target.closest('.noa-art');
    if (!el) return;
    var uid = +el.dataset.uid;
    var it = S.items.filter(function (x) { return x.uid === uid; })[0];
    if (!it) return;
    sel = uid;
    drag = { uid: uid, sx: e.clientX, sy: e.clientY, ox: it.x, oy: it.y, k: pxPerCm() };
    renderItems();
    drag.el = inner.querySelector('.noa-art[data-uid="' + uid + '"]');
    if (drag.el) { drag.el.classList.add('is-drag'); drag.el.setPointerCapture(e.pointerId); }
    e.preventDefault();
  });
  inner.addEventListener('pointermove', function (e) {
    if (!drag) return;
    var it = S.items.filter(function (x) { return x.uid === drag.uid; })[0];
    if (!it) return;
    // RTL: dragging right on screen decreases x (distance from the right edge)
    it.x = drag.ox - (e.clientX - drag.sx) / drag.k;
    it.y = drag.oy + (e.clientY - drag.sy) / drag.k;
    clampItem(it);
    var k = pxPerCm();
    if (drag.el) { drag.el.style.right = (it.x * k) + 'px'; drag.el.style.top = (it.y * k) + 'px'; }
  });
  function endDrag() {
    if (!drag) return;
    if (drag.el) drag.el.classList.remove('is-drag');
    drag = null; save(); renderItems();
  }
  inner.addEventListener('pointerup', endDrag);
  inner.addEventListener('pointercancel', endDrag);

  /* ---------- controls ---------- */
  grid.addEventListener('click', function (e) {
    var b = e.target.closest('.noa-pick__item');
    if (b) { addItem(b.dataset.h); save(); }
  });
  tabs.addEventListener('click', function (e) {
    var b = e.target.closest('.noa-pick__tab');
    if (b) { filter = b.dataset.c; renderPicker(); }
  });
  $('noaBgStrip').addEventListener('click', function (e) {
    var b = e.target.closest('.noa-bg');
    if (!b || b.dataset.room === 'custom' && !CUSTOM) return;
    if (S.room === b.dataset.room) return;
    var oldW = WALL_W, oldH = WALL_H;
    S.room = b.dataset.room;
    fitLayout(); sizeStage();
    remapItems(oldW, oldH, WALL_W, WALL_H);
    renderAll(); save();
  });
  $('noaClear').addEventListener('click', function () {
    S.items = []; sel = null; renderItems(); save(); toast('הקיר נוקה');
  });
  $('noaFrames').addEventListener('click', function () {
    S.frames = !S.frames;
    this.setAttribute('aria-pressed', S.frames);
    this.textContent = 'מסגרות: ' + (S.frames ? 'פועל' : 'כבוי');
    renderItems(); save();
  });
  $('noaRuler').addEventListener('click', function () {
    S.ruler = !S.ruler;
    this.setAttribute('aria-pressed', S.ruler ? 'true' : 'false');
    this.textContent = S.ruler ? 'סרגל: פועל' : 'סרגל 100 ס״מ';
    renderItems();
  });
  $('noaTidy').addEventListener('click', function () {
    var gap = 5, totalW = S.items.reduce(function (a, it) {
      return a + BY_HANDLE[it.handle].sizes[it.si].w + gap;
    }, -gap);
    var x = (WALL_W - totalW) / 2;
    var midY = WALL_H / 2;   // centre the row on the region's own middle
    S.items.forEach(function (it) {
      var s = BY_HANDLE[it.handle].sizes[it.si];
      it.x = x; x += s.w + gap;
      it.y = midY - s.h / 2;
      clampItem(it);
    });
    renderItems(); save(); toast('סודר בשורה, מיושר למרכז');
  });
  $('noaEdSize').addEventListener('change', function () {
    var it = S.items.filter(function (x) { return x.uid === sel; })[0];
    if (!it) return;
    it.si = +this.value; clampItem(it); renderItems(); save();
  });
  $('noaEdDel').addEventListener('click', function () {
    S.items = S.items.filter(function (x) { return x.uid !== sel; });
    sel = null; renderItems(); save();
  });
  $('noaEdFront').addEventListener('click', function () {
    var i = -1;
    S.items.forEach(function (x, j) { if (x.uid === sel) i = j; });
    if (i > -1) S.items.push(S.items.splice(i, 1)[0]);
    renderItems(); save();
  });
  $('noaRoomCm').addEventListener('change', function () {
    var r = room();
    if (!r) return;
    var oW = WALL_W, oH = WALL_H;
    S.roomCm[r.k] = Math.min(Math.max(+this.value || r.wallCm, 60), 500);
    this.value = S.roomCm[r.k];
    fitLayout(); sizeStage(); remapItems(oW, oH, WALL_W, WALL_H); renderAll(); save();
  });

  $('noaUpload').addEventListener('change', function () {
    var f = this.files && this.files[0];
    this.value = '';
    if (!f) return;
    if (!/^image\//.test(f.type)) { toast('נא לבחור קובץ תמונה'); return; }
    if (f.size > 25 * 1024 * 1024) { toast('התמונה גדולה מדי (עד 25MB)'); return; }
    toast('טוען את התמונה…');
    var fr = new FileReader();
    fr.onerror = function () { toast('לא הצלחנו לקרוא את הקובץ'); };
    fr.onload = function () {
      var im = new Image();
      im.onerror = function () { toast('הקובץ אינו תמונה תקינה'); };
      im.onload = function () {
        // downscale before keeping it around: a phone photo as a data URL is heavy
        var MAX = 2000, sc = Math.min(1, MAX / Math.max(im.naturalWidth, im.naturalHeight));
        var cv = document.createElement('canvas');
        cv.width = Math.round(im.naturalWidth * sc);
        cv.height = Math.round(im.naturalHeight * sc);
        cv.getContext('2d').drawImage(im, 0, 0, cv.width, cv.height);
        var oldW = WALL_W, oldH = WALL_H;
        CUSTOM = {
          k: 'custom', label: 'החדר שלי',
          img: cv.toDataURL('image/jpeg', 0.88),
          ratio: cv.width / cv.height,
          wallCm: S.roomCm.custom || 300,
          reg: { x: 0, y: 0, w: 1, h: 1 }
        };
        S.room = 'custom';
        fitLayout(); sizeStage();
        remapItems(oldW, oldH, WALL_W, WALL_H);
        renderAll(); save();
        // Ask for the real wall width. Without it the sizes on THEIR wall are a
        // guess, and this is the one flow where the visitor actually knows the number.
        $('noaCalibCm').value = Math.round(roomWidthCm());
        $('noaCalib').classList.add('is-on');
        $('noaCalibCm').focus();
        toast('החדר שלכם נטען — עכשיו נכייל את המידות');
      };
      im.src = fr.result;
    };
    fr.readAsDataURL(f);
  });

  function applyCalibration(cm) {
    var r = baseRoom();
    if (!r) return;
    var oW = WALL_W, oH = WALL_H;
    S.roomCm[r.k] = Math.min(Math.max(cm || r.wallCm, 60), 500);
    fitLayout(); sizeStage(); remapItems(oW, oH, WALL_W, WALL_H); renderAll(); save();
  }
  $('noaCalibOk').addEventListener('click', function () {
    applyCalibration(+$('noaCalibCm').value);
    $('noaCalib').classList.remove('is-on');
    toast('כויל — הגדלים על הקיר שלכם מדויקים עכשיו');
  });
  $('noaCalibCm').addEventListener('keydown', function (e) {
    if (e.key === 'Enter') $('noaCalibOk').click();
  });

  $('noaShare').addEventListener('click', function () {
    var url = location.origin + location.pathname + '#w=' + encode();
    history.replaceState(null, '', url);
    if (navigator.clipboard) navigator.clipboard.writeText(url).then(
      function () { toast('הקישור הועתק'); }, function () { toast('הקישור בשורת הכתובת'); });
    else toast('הקישור בשורת הכתובת');
  });

  /* PNG export — composites the room photo and the art. Shopify's CDN allows CORS,
     so toDataURL is not tainted; a single image that refuses to load is skipped
     rather than failing the whole download. */
  $('noaPng').addEventListener('click', function () {
    var btn = this; btn.disabled = true; btn.textContent = 'מייצא…';
    var r = room();
    var loadImg = function (src) {
      return new Promise(function (res) {
        var im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = function () { res(im); };
        im.onerror = function () { res(null); };
        im.src = src;
      });
    };
    var jobs = S.items.map(function (it) {
      var p = BY_HANDLE[it.handle], s = p.sizes[it.si];
      return loadImg(p.img).then(function (im) { return im && { im: im, it: it, s: s }; });
    });
    Promise.all([r ? loadImg(r.k === 'custom' ? r.img : sized(r.img, 2000)) : null].concat(jobs))
      .then(function (all) {
        var bgIm = all[0], list = all.slice(1).filter(Boolean);
        var cv = document.createElement('canvas'), k, ox = 0, oy = 0, g;
        if (r && bgIm) {
          cv.width = bgIm.naturalWidth; cv.height = bgIm.naturalHeight;
          g = cv.getContext('2d');
          g.drawImage(bgIm, 0, 0);
          k = cv.width / WALL_W;   // the surface is the whole photo now
        } else {
          k = 4;
          cv.width = Math.round(WALL_W * k); cv.height = Math.round(WALL_H * k);
          g = cv.getContext('2d');
          g.fillStyle = '#e9e4dc'; g.fillRect(0, 0, cv.width, cv.height);
        }
        var regW = cv.width;
        list.forEach(function (o) {
          var s = o.s, w = s.w * k, h = s.h * k;
          var x = ox + regW - (o.it.x * k) - w;   // RTL x is from the region's right edge
          var y = oy + o.it.y * k;
          g.save();
          g.shadowColor = 'rgba(0,0,0,.3)'; g.shadowBlur = 9 * k / 4; g.shadowOffsetY = 4 * k / 4;
          if (S.frames) {
            var pad = Math.min(s.w, s.h) * k * 0.035, mat = Math.min(s.w, s.h) * k * 0.055;
            g.fillStyle = '#b98a4b'; g.fillRect(x, y, w, h);
            g.shadowColor = 'transparent';
            g.fillStyle = '#fdfdfb'; g.fillRect(x + pad, y + pad, w - 2 * pad, h - 2 * pad);
            drawCover(g, o.im, x + pad + mat, y + pad + mat, w - 2 * (pad + mat), h - 2 * (pad + mat));
          } else {
            drawCover(g, o.im, x, y, w, h);
          }
          g.restore();
        });
        try {
          var a = document.createElement('a');
          a.download = 'noa-maccabi-art-wall.png';
          a.href = cv.toDataURL('image/png');
          a.click();
          toast('התמונה הורדה');
        } catch (e) { toast('הייצוא נחסם בדפדפן — נסו צילום מסך'); }
        btn.disabled = false; btn.textContent = 'הורדת תמונה';
      });
  });

  function drawCover(g, im, x, y, w, h) {
    var ir = im.naturalWidth / im.naturalHeight, tr = w / h, sw, sh, sx, sy;
    if (ir > tr) { sh = im.naturalHeight; sw = sh * tr; sx = (im.naturalWidth - sw) / 2; sy = 0; }
    else { sw = im.naturalWidth; sh = sw / tr; sx = 0; sy = (im.naturalHeight - sh) / 2; }
    g.drawImage(im, sx, sy, sw, sh, x, y, w, h);
  }

  $('noaBuy').addEventListener('click', function () {
    if (!S.items.length) return;
    var qty = {};
    S.items.forEach(function (it) {
      var id = BY_HANDLE[it.handle].sizes[it.si].id;
      qty[id] = (qty[id] || 0) + 1;
    });
    var btn = this; btn.disabled = true; btn.textContent = 'מוסיף…';
    fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: Object.keys(qty).map(function (id) { return { id: +id, quantity: qty[id] }; })
      })
    }).then(function (res) {
      if (!res.ok) throw new Error('cart');
      location.href = '/cart';
    }).catch(function () {
      btn.disabled = false; btn.textContent = 'הוספת הקיר לעגלה';
      toast('לא הצלחנו להוסיף לעגלה, נסו שוב');
    });
  });

  var tId;
  function toast(msg) {
    var t = $('noaToast');
    t.textContent = msg; t.classList.add('is-on');
    clearTimeout(tId); tId = setTimeout(function () { t.classList.remove('is-on'); }, 2200);
  }
  function save() { history.replaceState(null, '', location.pathname + '#w=' + encode()); }

  /* ---------- boot ---------- */
  if (location.hash.indexOf('#w=') === 0) decode(location.hash.slice(3));
  $('noaFrames').setAttribute('aria-pressed', S.frames);
  $('noaFrames').textContent = 'מסגרות: ' + (S.frames ? 'פועל' : 'כבוי');
  renderPicker();
  renderAll();
  var rt;
  window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(renderAll, 140); });
  /* Hiding the Shopify preview bar resizes the page WITHOUT firing window.resize, so the
     fitted height went stale and the whole view broke. Watch the container instead. */
  if (window.ResizeObserver) {
    var lay0 = document.querySelector('.noa-wall__layout');
    if (lay0 && lay0.parentElement) {
      /* Width only. Reacting to height changes fed this observer the very heights the
         resize handler had just written, so it never settled. */
      var lastW = 0;
      new ResizeObserver(function (entries) {
        var w = Math.round(entries[0].contentRect.width);
        if (Math.abs(w - lastW) < 2) return;
        lastW = w;
        clearTimeout(rt);
        rt = setTimeout(function () { fitLayout(); sizeStage(); renderItems(); }, 120);
      }).observe(lay0.parentElement);
    }
  }
})();
